import { useMemo, useState } from "react";
import { Box, CircularProgress, Menu, MenuItem, Typography } from "@mui/material";
import { Layers } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_LAYERS_ADD,
  MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE,
  MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME,
  MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE,
  TEMPLATES_DELETE,
  TEMPLATES_PRODUCE_ADD_GROUP,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_SHAPE,
  TEMPLATES_PRODUCE_ADD_TEXT,
} from "../../../i18n/keysTranslations";
import UploadFileButton from "../../Buttons/UploadFileButton";
import { IMAGE_FORMATS } from "../../../utils/constants";
import { useDispatchApp } from "../../../lib/redux";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { findNodePath } from "../../../features/creativeTemplates/utils/layerTree";
import SortableNestedLayerList from "../../../features/creativeTemplates/editor/SortableNestedLayerList";

const InstanceLayersSidebar = ({
  nodes = {},
  nodeLayouts = {},
  items = [],
  selectedID,
  expandedIDs,
  onSelectLayer,
  onRenameLayer,
  onToggleExpand,
  onReorderSiblings,
  onAddLayer,
  onDeleteLayer,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const [addMenuEl, setAddMenuEl] = useState(null);
  const [isAddingLayer, setIsAddingLayer] = useState(false);
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const hasLayers = items.length > 0;
  const selectedParentID =
    findNodePath({ nodes, nodeID: selectedID })?.parentID || null;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          minHeight: 0,
        }}
      >
        {hasLayers ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                {t(MODAL_IMAGE_EDIT_LAYERS_LIST_TITLE)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  component="button"
                  type="button"
                  aria-busy={isAddingLayer}
                  disabled={isAddingLayer}
                  onClick={(event) => {
                    if (isAddingLayer) return;
                    setAddMenuEl(event.currentTarget);
                  }}
                  sx={{
                    color: "#111",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isAddingLayer ? "default" : "pointer",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    border: "none",
                    bgcolor: "transparent",
                    p: 0,
                    fontFamily: "inherit",
                    minWidth: 16,
                    minHeight: 16,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    pointerEvents: isAddingLayer ? "none" : "auto",
                  }}
                >
                  {isAddingLayer ? (
                    <CircularProgress size={14} sx={{ color: "#111" }} />
                  ) : (
                    `+ ${t(MODAL_IMAGE_EDIT_LAYERS_ADD)}`
                  )}
                </Box>
              </Box>
            </Box>
            <Menu
              anchorEl={addMenuEl}
              open={Boolean(addMenuEl)}
              onClose={() => setAddMenuEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setAddMenuEl(null);
                  onAddLayer({ type: "text" });
                }}
              >
                {t(TEMPLATES_PRODUCE_ADD_TEXT)}
              </MenuItem>
              <MenuItem
                component="label"
                htmlFor="instance-layer-import-images"
                onClick={() => setAddMenuEl(null)}
              >
                {t(TEMPLATES_PRODUCE_ADD_IMAGE)}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuEl(null);
                  onAddLayer({ type: "shape" });
                }}
              >
                {t(TEMPLATES_PRODUCE_ADD_SHAPE)}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuEl(null);
                  onAddLayer({ type: "group" });
                }}
              >
                {t(TEMPLATES_PRODUCE_ADD_GROUP)}
              </MenuItem>
            </Menu>
            <UploadFileButton
              id="instance-layer-import-images"
              acceptedFormats={IMAGE_FORMATS}
              multiple={false}
              pathStorage={`socialMedia/${userID}/`}
              textUpload={MODAL_IMAGE_EDIT_LAYERS_ADD}
              colorUpload="secondary"
              isHidden={true}
              disabled={isAddingLayer}
              onFileSelect={(files) => {
                if (files?.length) setIsAddingLayer(true);
              }}
              onChange={(urls) => {
                const url = Array.isArray(urls) ? urls[0] : urls;
                if (url) onAddLayer({ type: "image", url });
              }}
              onUploadSettled={() => setIsAddingLayer(false)}
            />
            <SortableNestedLayerList
              nodes={nodes}
              nodeLayouts={nodeLayouts}
              itemsById={itemsById}
              selectedID={selectedID}
              expandedIDs={
                selectedParentID
                  ? { ...expandedIDs, [selectedParentID]: true }
                  : expandedIDs
              }
              onSelect={onSelectLayer}
              onRename={onRenameLayer}
              onToggleExpand={onToggleExpand}
              onReorder={onReorderSiblings}
              onDelete={onDeleteLayer}
              fallbackName={t(MODAL_IMAGE_EDIT_LAYERS_FALLBACK_NAME, {
                index: 1,
              })}
              deleteLabel={t(TEMPLATES_DELETE)}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Box sx={{ textAlign: "center", maxWidth: 320 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "#000",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Layers size={24} color="#fff" />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t(MODAL_IMAGE_EDIT_LAYERS_EMPTY_TITLE)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InstanceLayersSidebar;
